import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import { AuthConfigurationService } from '../auth/auth-configuration.service';

/** Completes POST /whatsapp/link/confirm after Cognito sign-in on the connect flow. */
@Injectable({ providedIn: 'root' })
export class WhatsappLinkService {
  private inFlight = false;

  constructor(
    private http: HttpClient,
    private authConfigService: AuthConfigurationService
  ) {}

  async completePendingLink(linkSessionId: string): Promise<{ tenantId: string }> {
    if (this.inFlight) {
      throw new Error('Link confirm already in progress');
    }
    const session = (linkSessionId || '').trim();
    if (!session) {
      throw new Error('Missing WhatsApp link session');
    }
    if (!this.authConfigService.configureAmplifyAuth()) {
      throw new Error('Tenant sign-in is not configured');
    }

    this.inFlight = true;
    try {
      const sesh = await Auth.currentSession();
      if (!sesh?.isValid?.() || !sesh.isValid()) {
        throw new Error('No valid Cognito session');
      }
      const refreshToken = this.refreshTokenFromSession(sesh);
      if (!refreshToken) {
        throw new Error('Could not read Cognito refresh token');
      }

      const tenantApi = (localStorage.getItem('apiGatewayUrl') || '').replace(/\/?$/, '');
      if (!tenantApi) {
        throw new Error('Missing tenant API URL');
      }

      // Authorization: Bearer <id token> is added by AuthInterceptor (same as getProduct).
      const url = `${tenantApi}/whatsapp/link/confirm`;
      try {
        const result = await this.http
          .post<{ ok?: boolean; tenantId?: string; error?: string }>(
            url,
            { session, refreshToken },
            { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
          )
          .toPromise();

        return { tenantId: result?.tenantId || '' };
      } catch (err) {
        throw new Error(this.httpErrorMessage(err));
      }
    } finally {
      this.inFlight = false;
    }
  }

  private refreshTokenFromSession(sesh: {
    getRefreshToken?: () => { getToken?: () => string };
  }): string {
    const refresh = sesh?.getRefreshToken?.();
    return refresh && typeof refresh.getToken === 'function' ? refresh.getToken() : '';
  }

  private httpErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && body.error) {
        return String(body.error);
      }
      if (typeof body === 'string') {
        return body;
      }
      return err.message || `HTTP ${err.status}`;
    }
    return err instanceof Error ? err.message : 'Link failed';
  }
}
